import React, { useState } from "react";
import { FaSpinner } from "react-icons/fa";
import LanguageSelector from "./LanguageSelector";
import TextInput from "./TextInput";
import AudioOutput from "./AudioOutput";
import { translateText } from "../../../services/translationService";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import vector from "../../../images/Vector.png";

const MAX_TEXT_CHARACTERS = 600;

const ConversionSection = () => {
  const [fromLanguage, setFromLanguage] = useState("English");
  const [toLanguage, setToLanguage] = useState("Yoruba");
  const [inputText, setInputText] = useState("");
  const [translatedText, setTranslatedText] = useState("");
  const [loading, setLoading] = useState(false);

  const handleFromLanguageChange = (lang) => {
    setFromLanguage(lang);
    const availableLanguages =
      lang === "English"
        ? ["Yoruba", "Swahili", "Nigerian Pidgin"]
        : lang === "Yoruba"
          ? ["English"]
          : lang === "Swahili"
            ? ["English"]
            : ["English"];
    setToLanguage(availableLanguages[0]);
  };

  const handleToLanguageChange = (lang) => {
    setToLanguage(lang);
    const availableLanguages =
      lang === "English"
        ? ["Yoruba", "Swahili", "Nigerian Pidgin"]
        : lang === "Yoruba"
          ? ["English"]
          : lang === "Swahili"
            ? ["English"]
            : ["English"];
    setFromLanguage(availableLanguages[0]);
  };

  const handleSwapLanguages = () => {
    setFromLanguage(toLanguage);
    setToLanguage(fromLanguage);
  };

  const handleTextChange = (e) => {
    const nextValue = e.target.value;
    if (nextValue.length > MAX_TEXT_CHARACTERS) {
      setInputText(nextValue.slice(0, MAX_TEXT_CHARACTERS));
      return;
    }
    setInputText(nextValue);
  };

  const handleTextPaste = (e) => {
    const pasted = e.clipboardData?.getData("text") ?? "";
    const selectionStart = e.target.selectionStart ?? inputText.length;
    const selectionEnd = e.target.selectionEnd ?? inputText.length;
    const nextLength = inputText.length - (selectionEnd - selectionStart) + pasted.length;
    if (nextLength > MAX_TEXT_CHARACTERS) {
      toast.error(`Text is limited to ${MAX_TEXT_CHARACTERS} characters.`);
    }
  };

  const handleConvert = async () => {
    if (!inputText.trim()) return;
    if (inputText.length > MAX_TEXT_CHARACTERS) {
      toast.error(`Text must be ${MAX_TEXT_CHARACTERS} characters or fewer.`);
      return;
    }
    if (fromLanguage === toLanguage) {
      toast.error("Please select different languages.");
      return;
    }

    setLoading(true);
    try {
      const translation = await translateText(
        inputText,
        fromLanguage,
        toLanguage
      );
      setTranslatedText(translation);
    } catch (error) {
      toast.error("Translation failed. Please try again.");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row items-center justify-between w-full mx-auto gap-10">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Left Section */}
      <div className="flex-1 flex flex-col gap-4 w-full">
        <div className="bg-white rounded-lg border-[1.5px] border-[#e0e4e2] p-4 shadow-sm">
          <LanguageSelector
            selectedLanguage={fromLanguage}
            onChange={handleFromLanguageChange}
            options={["English", "Yoruba", "Swahili", "Nigerian Pidgin"]}
          />
        </div>

        <div className="bg-white rounded-md border-[1.5px] border-[#e0e4e2] p-4 min-h-[261px] flex flex-col justify-between shadow-md">
          <TextInput
            value={inputText}
            onChange={handleTextChange}
            onPaste={handleTextPaste}
            maxLength={MAX_TEXT_CHARACTERS}
          />
          <div className="flex items-center justify-between gap-3 mt-3">
            <span className={`text-sm ${inputText.length >= MAX_TEXT_CHARACTERS ? "text-red-500 font-medium" : "text-gray-400"}`}>
              {inputText.length}/{MAX_TEXT_CHARACTERS} characters
              {inputText.length >= MAX_TEXT_CHARACTERS ? " • Limit reached" : ""}
            </span>
          </div>
          <button
            onClick={handleConvert}
            disabled={loading || fromLanguage === toLanguage || inputText.length > MAX_TEXT_CHARACTERS}
            className={`px-8 py-3 rounded-2xl mt-4 self-center transition-all ${loading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-[#1e6c56] text-white hover:bg-[#175443]"
              }`}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <FaSpinner className="animate-spin" />
                Converting...
              </span>
            ) : (
              "Convert"
            )}
          </button>
        </div>
      </div>

      {/* Swap Icon */}
      <button
        className="md:flex items-center justify-center bg-white p-3 rounded-full border shadow-md flex-shrink-0"
        onClick={handleSwapLanguages}
      >
        <img src={vector} alt="swap" className="w-[26px] h-[26px]" />
      </button>

      {/* Right Section */}
      <div className="flex-1 flex flex-col gap-4 w-full">
        <div className="bg-white rounded-lg border-[1.5px] border-[#e0e4e2] p-4 shadow-sm">
          <LanguageSelector
            selectedLanguage={toLanguage}
            onChange={handleToLanguageChange}
            options={
              fromLanguage === "English"
                ? ["Yoruba", "Swahili", "Nigerian Pidgin"]
                : fromLanguage === "Yoruba"
                  ? ["English"]
                  : fromLanguage === "Swahili"
                    ? ["English"]
                    : ["English"]
            }
          />
        </div>

        <div className="bg-white rounded-md border-[1.5px] border-[#e0e4e2] p-4 min-h-[261px] flex flex-col justify-between shadow-md">
          {loading ? (
            <div className="w-full min-h-[150px] bg-gray-100 p-4 rounded-md animate-pulse flex flex-col gap-3">
              <div className="h-6 bg-gray-300 rounded w-3/4"></div>
              <div className="h-6 bg-gray-300 rounded w-2/3"></div>
              <div className="h-6 bg-gray-300 rounded w-5/6"></div>
              <div className="h-6 bg-gray-300 rounded w-1/2"></div>
            </div>
          ) : (
            <AudioOutput text={translatedText} />
          )}

          <div className="flex justify-center gap-3 mt-4">
            <button
              className="bg-[#1e6c56] text-white px-8 py-3 rounded-2xl hover:bg-[#175443] transition-all"
              onClick={() => {
                if (translatedText) {
                  navigator.clipboard.writeText(translatedText);
                  toast.success("Text copied!");
                }
              }}
              disabled={loading}
            >
              Copy text
            </button>

            <button
              className="border-[1.5px] border-[#e0e4e2] px-8 py-3 rounded-2xl hover:bg-gray-100 transition-all"
              onClick={() => {
                setTranslatedText("");
                setInputText("");
              }}
              disabled={loading}
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversionSection;
